import angular, { IComponentOptions, IPromise, IQService, ITimeoutService, equals, noop } from "angular";
import { IModalScope, IModalStackService } from "angular-ui-bootstrap";
import { StateParams, StateService, TransitionOptions } from "@uirouter/angularjs";
import { GtmhubController } from "@gtmhub/core";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import dayjs, { Dayjs } from "@gtmhub/dayjs";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalModalsService } from "@gtmhub/goals/services/goal-modals.service";
import { GoalsChangedEventArgs, OkrEvents, goalSelected, metricSelected } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { LocalOrUtcTimeZone, dateDiffFromNow } from "@gtmhub/shared/filters/date-diff-from-now.filter";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { Ng1ConfirmDeleteService } from "@gtmhub/shared/services/ng1-confirm-delete.service";
import { INgRedux } from "@gtmhub/state-management/ng-redux";
import { getCurrentUserId } from "@gtmhub/users";
import { IPermissionsStoreState, hasPermissions } from "@gtmhub/users/redux";
import { Flavor, recommendation } from "@webapp/analytics/models/analytics.model";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { AssigneeSelectorType } from "@webapp/assignees/models/assignee.models";
import { CfMap } from "@webapp/custom-fields/models/custom-fields.models";
import { areCustomFieldsChanged } from "@webapp/custom-fields/utils";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { IAllowedActionsMap } from "@webapp/sessions/models/sessions.model";
import { NewTask, Task, TaskParentType } from "@webapp/tasks/models/tasks.models";
import { isNewTask, isTask } from "@webapp/tasks/utils/tasks.utils";
import { IGoal, SingleGoalSelectorInvokedBy } from "../../goals/models";
import { GoalService } from "../../goals/services/goal.service";
import { PermissionsService } from "../../users/permissions-service";
import { TaskService } from "../services/task.service";
import { TaskEvents } from "../task.events";

const focusElementByQuerySelector = (selector: string) => {
  document.querySelector<HTMLElement>(selector).focus();
};

type TaskFieldName = "name" | "description" | "status" | "ownerId" | "dueDate" | "parentId" | "customFields";

type TaskParentItem = IGoal | Metric;

interface ITaskSelectedParent {
  parent: TaskParentItem;
  type: TaskParentType;
  id: string;
}
export interface ITaskFormScope extends IModalScope, IExtendedRootScopeService {}

export class TaskFormCtrl extends GtmhubController {
  dateDiffFromNow: (input: string, timezone?: LocalOrUtcTimeZone, currentDate?: Dayjs) => string;
  indicators: {
    loading?: IIndicator;
    creating?: IIndicator;
    deleting?: IIndicator;
  };
  errorMessage: {
    name: boolean;
    ownerId: boolean;
    status: boolean;
    dueDate: boolean;
    parentId: boolean;
    description: boolean;
  };
  editMode: {
    name: boolean;
    dueDate: boolean;
    parent: boolean;
    description: boolean;
  };
  task: Task | NewTask;
  assigneeSelectorType: AssigneeSelectorType;
  parent: TaskParentItem;
  allowedActionsMap: IAllowedActionsMap;
  private taskPromise: IPromise<Task>;
  private taskViewId = "#single-task-view";
  private defaultNewTask: NewTask;
  private taskEvents: TaskEvents;
  private okrEvents: OkrEvents;
  persistedTaskFields: {
    name: string;
    description: string;
    customFields: CfMap;
  } = { name: "", description: "", customFields: {} };

  public static $inject = [
    "$rootScope",
    "$uibModalStack",
    "$q",
    "$scope",
    "$state",
    "$timeout",
    "GoalService",
    "TaskService",
    "UIErrorHandlingService",
    "PermissionsService",
    "GoalModalsService",
    "$stateParams",
    "AnalyticsService",
    "MetricService",
    "$ngRedux",
    "Ng1ConfirmDeleteService",
  ];

  constructor(
    $rootScope: ITraceRootScopeService,
    private $uibModalStack: IModalStackService,
    private $q: IQService,
    private $scope: ITaskFormScope,
    private $state: StateService,
    private $timeout: ITimeoutService,
    private goalService: GoalService,
    private taskService: TaskService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private permissionsService: PermissionsService,
    private goalModalsService: GoalModalsService,
    private $stateParams: StateParams,
    private analyticsService: AnalyticsService,
    private metricService: MetricService,
    private $ngRedux: INgRedux,
    private confirmDeleteService: Ng1ConfirmDeleteService
  ) {
    super();

    this.dateDiffFromNow = dateDiffFromNow();

    this.taskEvents = new TaskEvents(this.$scope);

    this.defaultNewTask = this.generateNewTask();

    this.okrEvents = new OkrEvents(this.$scope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.editMode = {
      description: false,
      dueDate: false,
      name: true,
      parent: false,
    };

    this.errorMessage = {
      description: false,
      dueDate: false,
      name: false,
      ownerId: false,
      parentId: false,
      status: false,
    };

    this.indicators = {
      loading: { progress: true },
    };

    this.assigneeSelectorType = "user";
    this.featureAvailable = $rootScope.featureAvailable;
  }

  $onInit(): void {
    this.init();
  }

  featureAvailable: (name: string) => boolean;

  private getParentId(): string {
    if (this.$stateParams.parentType === "metric") {
      return this.$stateParams.metricId;
    }

    // defaults to parent type 'goal'
    return this.$stateParams.parentId || this.$stateParams.id || "";
  }

  private init(): IPromise<unknown> {
    const taskId = this.$stateParams.taskId;
    if (taskId) {
      this.taskPromise = this.taskService.getOne(taskId);
    }

    return this.$q
      .all({
        task: taskId ? this.taskPromise : this.$q.resolve(this.generateNewTask()),
      })
      .then(
        ({ task }) => {
          const promiseArr: IPromise<unknown>[] = [];

          this.task = task;
          this.persistedTaskFields.name = this.task.name;
          this.persistedTaskFields.description = this.task.description;
          this.persistedTaskFields.customFields = angular.copy(this.task.customFields);

          if (task.parentId) {
            promiseArr.push(this.getParent(task.parentId, task.parentType));
          } else if (isNewTask(task)) {
            this.toggleEditMode("name");
            this.$timeout(() => {
              focusElementByQuerySelector(`${this.taskViewId} [name='name']`);
            });
          }

          this.toggleEditMode("name"); // focusing task name when creating brand new task

          if (taskId) {
            promiseArr.push(this.checkPermissions());
          } else {
            this.allowedActionsMap = {
              read: true,
              update: true,
              create: true,
              delete: true,
              modifyPermissions: true,
            };
          }

          this.resolveInitPromises(promiseArr, taskId, task);
        },
        (error) => {
          this.taskPromise = null;
          this.indicators.loading = { error };
        }
      );
  }

  private resolveInitPromises = (promiseArr: IPromise<unknown>[], taskId: string, task: Task | NewTask): IPromise<void> | void => {
    if (promiseArr.length) {
      return this.$q.all(promiseArr).then(
        () => {
          delete this.indicators.loading;
          if (isTask(task)) {
            this.taskEvents.broadcastTaskVisited(task);
          }
        },
        (error) => {
          this.indicators.loading = { error };
        }
      );
    } else {
      delete this.indicators.loading;
    }
  };

  private generateNewTask(): NewTask {
    return {
      name: "",
      ownerId: getCurrentUserId(),
      status: "todo",
      description: "",
      parentId: this.getParentId(),
      parentType: this.$stateParams.parentType,
    };
  }

  openGoal(): void {
    const currentState = this.$state.current.name.split(".");
    const fromState = currentState[currentState.length - 3];

    switch (fromState) {
      // gtmhub.goals.alignmentSidebar.tree._goalSidebar.goal._task.task
      // gtmhub.goals.all._goalSidebar.goal._task.task
      case "goal":
        this.$state.go("^.^");
        break;
      case "list":
        this.$state.go("gtmhub.list.goals._goalSidebar.goal", { id: this.parent.id });
        break;
      default:
        this.$state.go("^.^._goalSidebar.goal", { id: this.parent.id });
        break;
    }
  }

  openMetric(): void {
    const currentState = this.$state.current.name.split(".");
    const fromState = currentState[currentState.length - 3];

    switch (fromState) {
      case "metric":
        this.$state.go("^.^");
        break;
      case "list":
        this.$state.go("gtmhub.list.goals._metric.metric", { metricId: this.parent.id });
        break;
      default:
        this.$state.go("^.^._metric.metric", { metricId: this.parent.id });
        break;
    }
  }

  changeStatus(status: "todo" | "inprogress" | "done"): void {
    if (this.task.status === status) {
      return;
    }

    if (isNewTask(this.task)) {
      this.task.status = status;
    }
    this.updateField({ status }, "status").catch(noop);
  }

  dismissTopModal(): void {
    this.$uibModalStack.dismiss(this.$uibModalStack.getTop().key);
  }

  openConfirmDeleteModal(): void {
    this.confirmDeleteService.confirmDeleteItem({ itemKey: "task", itemToCase: true }).subscribe(() => this.deleteTask(this.task as Task));
  }

  private deleteTask(task: Task): void {
    this.indicators = {
      deleting: { progress: true },
    };

    this.taskService.delete(task.id).then(
      () => {
        this.taskEvents.broadcastTaskDeleted({ task, sessionId: this.getTaskSessionId() });
        this.dismissTopModal();
        this.task = null;

        delete this.indicators.deleting;
      },
      (error) => (this.indicators.deleting = { error })
    );
  }

  private getParent(parentId: string, parentType: string): IPromise<unknown> {
    this.indicators.loading = { progress: true };
    const params = {
      fields: "name,sessionId",
    };
    const metricParams = {
      filter: {
        $and: [{ _id: { $in: [parentId] } }],
      },
      fields: "name,sessionId",
      limit: 1,
    };
    const getParentPromise: IPromise<TaskParentItem> =
      parentType === "metric" ? this.metricService.getMetricsV2(metricParams).then((metrics) => metrics.items[0]) : this.goalService.getGoal(parentId, params);

    // TODO: refactor naming in ctrl and view s.t. parent is a more generic (not goal, not metric)
    return getParentPromise.then(
      (entity) => {
        delete this.indicators.loading;
        this.parent = entity;
        if (isNewTask(this.task)) {
          this.toggleEditMode("name");
          this.$timeout(() => {
            focusElementByQuerySelector(`${this.taskViewId} [name='name']`);
          });
        }
      },
      (error) => (this.indicators.loading = { error })
    );
  }

  private checkPermissions(): IPromise<unknown> {
    const task = this.task as Task;
    return this.permissionsService.getCurrentUserAllowedActionsPerItem(task.id, "task").then(
      (actions) => {
        this.allowedActionsMap = actions.currentUserActions.reduce(
          (actionsMap, action) => (actionsMap[action] ? actionsMap : ((actionsMap[action] = true), actionsMap)),
          {}
        );
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  }

  keyup(event: KeyboardEvent, field: string, type?: string): void {
    if (type !== "textarea") {
      if (event.key === "Enter") {
        document.querySelector<HTMLElement>(`${this.taskViewId} [name=${field}]`).blur();
      } else if (event.key === "Tab") {
        const input = document.querySelector<HTMLInputElement>(`${this.taskViewId} [ng-model='task.${field}']`);
        input.selectionStart = input.selectionEnd;
      }
    }
  }

  keydown(event: KeyboardEvent, field: string): void {
    // prevents name text input from going on new line
    if (field === "name" && event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  paste(event: ClipboardEvent, field: string): void {
    if (field === "name") {
      event.stopPropagation();
      event.preventDefault();
      const textToPaste = event.clipboardData.getData("text/plain");
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      selection.deleteFromDocument();
      const range = selection.getRangeAt(0);
      range.insertNode(document.createTextNode(textToPaste));
      selection.collapseToEnd();
    }
  }

  ownerChanged(ids: string[]): void {
    const newOwnerIds = ids && ids[0];
    if (this.task.ownerId === newOwnerIds) {
      return;
    }

    this.task.ownerId = newOwnerIds;
    this.updateField({ ownerId: this.task.ownerId }, "ownerId").then(() => this.checkPermissions(), noop);
  }

  setDueDate(dueDate: string): void {
    if (dueDate) {
      this.updateField({ dueDate: dayjs(dueDate).format() }, "dueDate").catch(noop);
    } else {
      // if the dueDate is already removed, do not send patch request
      if (!this.task.dueDate) {
        return;
      }
      delete this.task.dueDate;
      this.updateField({ unset: { dueDate: true } }, "dueDate").catch(noop);
    }
  }

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === metricSelected) {
      this.onMetricSelected(args.detail.metric, args.detail.invokedById, args.detail.invokedBy);
      return;
    }
    if (args.reason === goalSelected) {
      this.onGoalSelected(args.detail.goal, args.detail.invokedById, args.detail.invokedBy);
    }
  };

  private onGoalSelected(selectedGoal: IGoal, invokedById: string, invokedBy: SingleGoalSelectorInvokedBy): void {
    // if we are selecting a goal for a not yet created task its id is undefined later converted to "" in goal selector component
    if (this.isParentChosenForTask(invokedById, invokedBy)) {
      const selected: ITaskSelectedParent = {
        parent: selectedGoal,
        type: "goal",
        id: selectedGoal.id,
      };

      this.updateTaskParent(selected);
    }
  }

  private onMetricSelected(metric: Metric, invokedById: string, invokedBy: SingleGoalSelectorInvokedBy): void {
    if (this.isParentChosenForTask(invokedById, invokedBy)) {
      const selected: ITaskSelectedParent = {
        parent: metric,
        type: "metric",
        id: metric.id,
      };

      this.updateTaskParent(selected);
    }
  }

  private isParentChosenForTask(invokedById: string, invokedBy: string): boolean {
    return ((isTask(this.task) && this.task.id === invokedById) || (isNewTask(this.task) && invokedById === "")) && invokedBy === "TaskSingleView";
  }

  private updateTaskParent(selected: ITaskSelectedParent): void {
    this.task.parentId = selected.id;
    this.task.parentType = selected.type;
    this.parent = selected.parent;

    this.updateField({ parentId: selected.id, parentType: selected.type }, "parentId").then(() => this.checkPermissions(), noop);
  }

  changeParent(): void {
    if (this.task.parentId) {
      this.task.parentId = "";
      this.task.parentType = null;
      this.parent = null;

      this.updateField({ parentId: this.task.parentId, parentType: this.task.parentType }, "parentId").then(() => this.navigateToParentSelector(), noop);
    } else {
      this.navigateToParentSelector();
    }
  }

  private navigateToParentSelector(): void {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (this.taskPromise) {
      this.taskPromise.then(() => this.openParentSelector());
    } else {
      this.openParentSelector();
    }
  }

  private openParentSelector(): void {
    const invokedById = isTask(this.task) ? this.task.id : undefined;
    this.goalModalsService.openGoalSelectModal({ unselectableId: this.task.parentId, invokedById, invokedBy: "TaskSingleView" });
  }

  private showErrorMessage(field: TaskFieldName): void {
    this.errorMessage[field] = true;
    this.$timeout(() => {
      this.errorMessage[field] = false;
    }, 5000);
  }

  clone(): void {
    const clonedTask = angular.copy(this.task) as Task;
    this.taskPromise = this.create(clonedTask, { cloned: true }).catch((error) => {
      this.indicators.creating = { error };

      return clonedTask;
    });
  }

  toggleArchive(): void {
    const task = this.task as Task;
    this.updateField({ archived: !task.archived }, "name").catch(noop);
  }

  toggleEditMode(field: string, { skipEdit } = { skipEdit: false }): void {
    // for some cases it is necessary to specifically define the mode's state
    // or it may be unwillingly change due to propagation (e.g., datepicker)
    this.editMode[field] = !skipEdit ? !this.editMode[field] : false;

    if (this.editMode[field] === true) {
      this.$timeout(() => focusElementByQuerySelector(`${this.taskViewId} [name=${field}]`));
    }
  }

  updateDescription(): void {
    if (this.persistedTaskFields.description === this.task.description) {
      return;
    }

    this.updateField({ description: this.task.description }, "description").catch(noop);
  }

  updateName(): void {
    // prevents the update of a task's title to an empty string
    if (!this.task.name) {
      this.task.name = this.persistedTaskFields.name;
    } else if (this.task.name !== this.persistedTaskFields.name) {
      this.updateField({ name: this.task.name }, "name").catch(noop);
      this.persistedTaskFields.name = this.task.name;
    }
  }

  public isCustomFieldDisabled = (): boolean => {
    return !hasPermissions(this.$ngRedux.getState<IPermissionsStoreState>(), "ManageTasks");
  };

  updateCustomFields(customFields: CfMap): void {
    if (!this.persistedTaskFields.customFields || areCustomFieldsChanged(customFields, this.persistedTaskFields.customFields)) {
      this.updateField({ customFields: customFields }, "customFields")
        .then(() => {
          this.persistedTaskFields.customFields = customFields;
        })
        .catch(noop);
    }
  }

  private updateField(update: Partial<Task>, errorMessageField: TaskFieldName): IPromise<Task> {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (this.taskPromise) {
      const updatePromise: IPromise<{ task: Task; error: IUIError }> = this.taskPromise.then((task) =>
        this.patch(task.id, update)
          .then((updatedTask) => ({ task: updatedTask, error: null }))
          .catch((error) => ({ task, error }))
      );
      this.taskPromise = updatePromise.then(({ task }) => task);

      return updatePromise.then(({ task, error }) => {
        if (error) {
          this.showErrorMessage(errorMessageField);

          return this.$q.reject(error);
        }

        this.task = task;
        return task;
      });
    } else {
      if (equals(this.task, this.defaultNewTask)) {
        return this.$q.reject();
      }

      const newTask = this.task as NewTask;
      this.taskPromise = this.create(newTask).catch((error) => {
        this.taskPromise = null;

        if (this.task.name) {
          this.indicators.creating = { error };
        }

        return this.$q.reject(error);
      });
    }

    return this.taskPromise;
  }

  private create(task: NewTask, options: { cloned?: boolean } = {}): IPromise<Task> {
    if (!this.task.name) {
      return this.$q.reject();
    }
    if (!this.persistedTaskFields.name) {
      this.persistedTaskFields.name = this.task.name;
    }
    if (!this.persistedTaskFields.description) {
      this.persistedTaskFields.description = this.task.description;
    }
    this.indicators.creating = { progress: true };

    return this.taskService.create(task, options && options.cloned && { gtmhubAdditionalParams: { cloned: options.cloned } }).then((newTask) => {
      Object.assign(this.task, newTask);

      this.trackRecommendationHelpfulness(newTask);
      // notify: false is used to prevent reinitializing the controller when creating a task
      // location: "replace" means that create task state will be replaced in browser history by edit task state
      // this way clicking back will not lead to create task state
      const stateOptions: TransitionOptions = options.cloned ? { notify: false } : { location: "replace", notify: false };
      this.$state.go(".", { taskId: newTask.id }, stateOptions).then(() => {
        delete this.indicators.creating;
        // updating current user allowed actions after task is created and state is changed
        this.checkPermissions();
      });

      this.taskEvents.broadcastTaskCreated({ task: newTask, sessionId: this.getTaskSessionId() });

      return newTask;
    });
  }

  private trackRecommendationHelpfulness(task: Task): void {
    // recommendation is helpful if user creates task and does not remove himself as an owner
    this.analyticsService.track(recommendation, { is_useful: task.ownerId === getCurrentUserId(), flavor: Flavor.SMART_DEFAULT, action: "creates_task;owner" });
  }

  private patch(taskId: string, partialTask: Partial<Task>): IPromise<Task> {
    return this.taskService.update(taskId, partialTask).then((updatedTask) => {
      this.taskEvents.broadcastTaskUpdated({ task: updatedTask, update: partialTask, sessionId: this.getTaskSessionId() });

      return updatedTask;
    });
  }

  private getTaskSessionId(): string {
    return this.parent ? this.parent.sessionId : null;
  }
}

export const TaskFormComponent: IComponentOptions = {
  template: require("./task-form.html"),
  controller: TaskFormCtrl,
  bindings: {},
};
